import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { apiGet } from '@/api/client';
import type { CompanyLocation, CompanyMember, MeDepartment, UserActivation } from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { Badge } from '@/app/components/ui/Badge';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { DataTable, type DataTableColumn } from '@/app/components/ui/DataTable';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { Input, Select } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useActiveCompany } from '@/features/company/CompanyContext';
import { useMe } from '@/features/me/MeContext';
import { visibleDepartments } from '@/features/me/permissions';
import { fetchPilotMembership, type PilotMembership } from '@/features/pilots/api';
import { ImportUsersModal } from '@/features/users/ImportUsersModal';
import { InviteUserModal } from '@/features/users/InviteUserModal';
import {
  ChangeDepartmentSelect,
  ChangeLocationSelect,
  DeactivateToggle,
  InviteButton,
  UnlinkFromPilotButton,
} from '@/features/users/memberActions';
import { formatDate } from '@/lib/format';
import { useAsync } from '@/lib/useAsync';

/**
 * `/users` — the *Users* section, always scoped to the active company
 * (§ Bedrijfscontext): a real company's own member table, or (no company at
 * all) a coach's own department member lists. A plain member without any
 * administrative role never reaches a member list at all — besloten,
 * § Zichtbaarheid.
 */
export function UsersPage() {
  const { company } = useActiveCompany();

  if (company === null) {
    return <PersonalUsers />;
  }
  return <CompanyUsers companyId={company.id} companyName={company.name} />;
}

function NoScope() {
  const { t } = useTranslation();
  return (
    <section className="page">
      <PageHeader title={t('users.page.title')} />
      <p className="text-ink text-sm">{t('users.page.noScope')}</p>
    </section>
  );
}

function DepartmentMemberListLinks({ departments }: { departments: MeDepartment[] }) {
  const { t } = useTranslation();
  if (departments.length === 0) return null;
  return (
    <Card aria-label={t('users.page.departmentListsTitle')}>
      <CardHeader title={t('users.page.departmentListsTitle')} />
      <ul className="flex flex-col gap-2">
        {departments.map((department) => (
          <li key={department.id}>
            <Link className="text-accent text-sm" to={`/departments/${department.id}`}>
              {department.name}
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function PersonalUsers() {
  const { t } = useTranslation();
  const { me, permissions } = useMe();
  const departments = visibleDepartments(permissions, null, me.departments ?? []).filter((d) =>
    permissions.canViewDepartmentMembers({ id: d.id, company_id: d.company_id }),
  );

  if (departments.length === 0) {
    return <NoScope />;
  }

  return (
    <section className="page flex flex-col gap-8">
      <PageHeader title={<span className="uppercase">{t('users.page.title')}</span>} />
      <DepartmentMemberListLinks departments={departments} />
    </section>
  );
}

function CompanyUsers({ companyId, companyName }: { companyId: string; companyName: string }) {
  const { t } = useTranslation();
  const { me, permissions } = useMe();
  const mayManageCompany = permissions.canManageCompanyUsers(companyId);
  const coachDepartments = visibleDepartments(
    permissions,
    companyId,
    (me.departments ?? []).filter((d) => d.company_id === companyId),
  ).filter((d) => permissions.canViewDepartmentMembers({ id: d.id, company_id: d.company_id }));

  if (!mayManageCompany && coachDepartments.length === 0) {
    return <NoScope />;
  }

  return (
    <section className="page flex flex-col gap-8">
      <PageHeader
        title={<span className="uppercase">{t('users.page.title')}</span>}
        lead={t('users.page.leadCompany', { company: companyName })}
      />

      {mayManageCompany && (
        <CompanyMembersSection companyId={companyId} companyName={companyName} />
      )}

      <DepartmentMemberListLinks departments={coachDepartments} />
    </section>
  );
}

const ACTIVATION_TONE: Record<UserActivation, 'outline' | 'mint' | 'warning' | 'danger'> = {
  invited: 'outline',
  activated: 'outline',
  active: 'mint',
  inactive: 'warning',
  deactivated: 'danger',
};

const CONSENT_TONE: Record<string, 'outline' | 'mint' | 'warning' | 'danger'> = {
  confirmed: 'mint',
  pending: 'warning',
  declined: 'danger',
  none: 'outline',
};

/**
 * The Medewerkers list (PLAN-FASE2.md WP-01): the extended member table with
 * search/department/location/status filters, the Uitnodigen and CSV
 * importeren modals, and per-row actions (opnieuw uitnodigen, afdeling
 * wijzigen, locatie wijzigen, deactiveren).
 */
function CompanyMembersSection({
  companyId,
  companyName,
}: {
  companyId: string;
  companyName: string;
}) {
  const { t } = useTranslation();
  const { permissions } = useMe();
  const [search, setSearch] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [status, setStatus] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const members = useAsync(
    () =>
      apiGet('/companies/{companyId}/users', {
        params: { companyId },
        query: {
          ...(search.trim() !== '' ? { search: search.trim() } : {}),
          ...(departmentId !== '' ? { department_id: departmentId } : {}),
          ...(locationId !== '' ? { location_id: locationId } : {}),
          ...(status !== '' ? { status: status as UserActivation } : {}),
        },
      } as never) as unknown as Promise<CompanyMember[]>,
    [companyId, search, departmentId, locationId, status],
  );

  const departments = useAsync(
    () =>
      apiGet('/companies/{companyId}/departments', { params: { companyId } }) as unknown as Promise<
        { id: string; name: string }[]
      >,
    [companyId],
  );
  // WP-04: who currently sits in an ongoing pilot's department, and through
  // which pilot — drives the Pilot column and the "Ontkoppelen van pilot"
  // action. Best-effort: a caller with no pilots.view anywhere in this
  // company simply sees "–" everywhere, same as an empty pilots list would.
  const pilotMembership = useAsync(
    () => fetchPilotMembership(companyId).catch(() => new Map<string, PilotMembership>()),
    [companyId],
  );

  const locations = useAsync(
    () =>
      apiGet('/companies/{companyId}/locations', {
        params: { companyId },
      }) as unknown as Promise<CompanyLocation[]>,
    [companyId],
  );

  // `?? []` alone isn't enough here: a malformed API response (wrong shape,
  // still 200) is truthy and would otherwise reach `.map()` below and crash
  // the whole page rather than degrade to an empty list.
  const departmentRows = useMemo(
    () => (Array.isArray(departments.data) ? departments.data : []),
    [departments.data],
  );
  const locationRows = useMemo(
    () => (Array.isArray(locations.data) ? locations.data : []),
    [locations.data],
  );
  const mayProvision = permissions.canProvisionInCompany(companyId);

  return (
    <Card padding="none" aria-label={t('users.page.membersOf', { company: companyName })}>
      <div className="p-6 pb-0">
        <CardHeader
          title={<span className="uppercase">{t('users.page.companyMembersCardTitle')}</span>}
          action={
            mayProvision ? (
              <div className="flex gap-2">
                <Button size="sm" onClick={() => setInviteOpen(true)}>
                  {t('users.page.invitePerson')}
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setImportOpen(true)}>
                  {t('users.page.importCsv')}
                </Button>
              </div>
            ) : undefined
          }
        />
        <div className="mb-6 flex flex-wrap gap-2" role="search">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('users.page.searchPlaceholder')}
            aria-label={t('users.page.searchPlaceholder')}
            className="max-w-56"
          />
          <Select
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
            aria-label={t('users.page.filterDepartment')}
            className="max-w-64 min-w-40"
          >
            <option value="">{t('users.page.allDepartments')}</option>
            {departmentRows.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
          <Select
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            aria-label={t('users.page.filterLocation')}
            className="max-w-64 min-w-40"
          >
            <option value="">{t('users.page.allLocations')}</option>
            {locationRows.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </Select>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label={t('users.page.filterStatus')}
            className="max-w-48"
          >
            <option value="">{t('users.page.allStatuses')}</option>
            {(
              ['invited', 'activated', 'active', 'inactive', 'deactivated'] as UserActivation[]
            ).map((value) => (
              <option key={value} value={value}>
                {t(`users.page.activation.${value}`)}
              </option>
            ))}
          </Select>
        </div>
      </div>
      {mayProvision && (
        <>
          <InviteUserModal
            open={inviteOpen}
            onClose={() => setInviteOpen(false)}
            companyId={companyId}
            onInvited={() => {
              setInviteOpen(false);
              members.reload();
            }}
          />
          <ImportUsersModal
            open={importOpen}
            onClose={() => setImportOpen(false)}
            companyId={companyId}
            onImported={() => members.reload()}
          />
        </>
      )}
      <AsyncSection
        state={members}
        label={t('users.page.membersOf', { company: companyName })}
        skeleton="table"
      >
        {(rows) =>
          rows.length === 0 ? (
            <div className="p-6 pt-0">
              <EmptyState text={t('users.page.empty')} />
            </div>
          ) : (
            <DataTable
              className="px-3 pb-6"
              rowKey={(m) => m.user_id}
              rows={rows}
              columns={
                [
                  {
                    key: 'name',
                    header: t('users.page.employee'),
                    render: (m) => (
                      <div className="flex flex-col">
                        <Link to={`/users/${m.user_id}`} className="text-accent">
                          {m.name}
                        </Link>
                        <span className="text-ink text-[13px]">{m.email}</span>
                      </div>
                    ),
                  },
                  {
                    key: 'department',
                    header: t('users.page.department'),
                    render: (m) =>
                      mayProvision ? (
                        <ChangeDepartmentSelect
                          userId={m.user_id}
                          departmentId={m.department_id}
                          departments={departmentRows}
                          onDone={() => members.reload()}
                        />
                      ) : (
                        (m.department_name ?? '—')
                      ),
                  },
                  {
                    key: 'location',
                    header: t('users.page.location'),
                    render: (m) =>
                      mayProvision ? (
                        <ChangeLocationSelect
                          userId={m.user_id}
                          locationId={m.location_id}
                          locations={locationRows}
                          onDone={() => members.reload()}
                        />
                      ) : (
                        (m.location_name ?? '—')
                      ),
                  },
                  {
                    key: 'activation',
                    header: t('users.page.activation.label'),
                    render: (m) => (
                      <Badge tone={ACTIVATION_TONE[m.activation]}>
                        {t(`users.page.activation.${m.activation}`)}
                      </Badge>
                    ),
                  },
                  {
                    key: 'lastSync',
                    header: t('users.page.lastSync'),
                    render: (m) => (m.last_sync ? formatDate(m.last_sync) : '—'),
                  },
                  {
                    key: 'consent',
                    header: t('users.page.consent'),
                    render: (m) => (
                      <Badge tone={CONSENT_TONE[m.consent] ?? 'outline'}>
                        {t(`users.page.consentStatus.${m.consent}`)}
                      </Badge>
                    ),
                  },
                  {
                    key: 'wearable',
                    header: t('users.page.wearable'),
                    render: (m) => (m.has_wearable ? t('users.page.yes') : t('users.page.no')),
                  },
                  {
                    key: 'pilot',
                    header: t('users.page.pilot'),
                    render: (m) => {
                      const membership = pilotMembership.data?.get(m.user_id);
                      return membership ? t('users.page.yes') : t('users.page.no');
                    },
                  },
                  {
                    key: 'account',
                    header: t('users.page.account'),
                    render: (m) =>
                      m.deactivated_at
                        ? t('users.page.accountDeactivated')
                        : t('users.page.accountActive'),
                  },
                  ...(mayProvision
                    ? [
                        {
                          key: 'actions',
                          header: t('users.page.actions'),
                          align: 'end' as const,
                          render: (m: CompanyMember) => {
                            const membership = pilotMembership.data?.get(m.user_id);
                            return (
                              <div className="flex flex-wrap justify-end gap-2">
                                {!m.has_login && (
                                  <InviteButton
                                    userId={m.user_id}
                                    onDone={() => members.reload()}
                                  />
                                )}
                                {membership && (
                                  <UnlinkFromPilotButton
                                    pilotId={membership.pilotId}
                                    departmentId={membership.departmentId}
                                    onDone={() => {
                                      members.reload();
                                      pilotMembership.reload();
                                    }}
                                  />
                                )}
                                <DeactivateToggle
                                  userId={m.user_id}
                                  deactivated={m.deactivated_at !== null}
                                  onDone={() => members.reload()}
                                />
                              </div>
                            );
                          },
                        },
                      ]
                    : []),
                ] as DataTableColumn<CompanyMember>[]
              }
            />
          )
        }
      </AsyncSection>
    </Card>
  );
}
